import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import apiService from "../services/apiService";
import stripeService from "../services/stripeService";
import PricingPlanCardLanding from "./PricingPlanCardLanding";
import { useOrganization } from "../context/OrganizationContext";
import { getSubscriptionPlanId } from "../utils/subscription";

function formatNumber(num) {
  if (num == null || num === "") return "Unlimited";
  const n = Number(num);
  if (Number.isNaN(n)) return String(num);
  return n.toLocaleString();
}

function buildPlanFeatures(plan) {
  const rows = [];

  if (plan.request_monthly != null && plan.request_monthly !== "") {
    const raw = plan.request_monthly;
    const n = Number(raw);
    if (!Number.isNaN(n) && n >= 1e12) {
      rows.push("Unlimited requests/month");
    } else {
      rows.push(`${formatNumber(raw)} requests/month`);
    }
  } else {
    rows.push("Unlimited requests/month");
  }

  if (plan.request_daily) {
    rows.push(`${formatNumber(plan.request_daily)} requests/day`);
  }

  if (plan.rps != null && plan.rps !== "") {
    rows.push(`${plan.rps} RPS`);
  }

  if (plan.support) {
    rows.push(plan.support);
  }

  if (plan.archive_support) {
    rows.push("Archive support");
  }

  return rows;
}

function planPriceDisplay(plan) {
  const p = plan.price_usd;
  if (p == null || p === "") return "Free";
  const n = Number(p);
  if (Number.isNaN(n)) return "Free";
  if (n === 0) return "$0";
  return `$${n.toFixed(0)}`;
}

const SubscriptionPlans = () => {
  const { subscription, currentOrganization } = useOrganization();
  const currentPlanId = getSubscriptionPlanId(subscription);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Owner-only: only the org owner can start a checkout. Mirrors the
  // backend gate on /stripe/create-checkout-session/. Non-owners still
  // see the plan grid (transparency on what's available) but every CTA
  // is replaced with a "Owner-only" disabled state.
  const userEmail = (localStorage.getItem("userEmail") || "").toLowerCase();
  const isOrgOwner = !!(
    currentOrganization?.owner_email &&
    currentOrganization.owner_email.toLowerCase() === userEmail
  );

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const list = await apiService.getPlans();
        setPlans(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSubscribe = async (plan) => {
    setSelectedPlan(plan);
    setIsProcessing(true);

    try {
      const successUrl = `${window.location.origin}/#/subscription/success`;
      const cancelUrl = `${window.location.origin}/#/subscription/cancel`;

      // ALWAYS pass the currently selected org's UUID. Without it the
      // backend used to default to the user's first-joined org, charging
      // the wrong workspace (2026-05-08 incident).
      const session = await stripeService.createCheckoutSession(
        plan.id,
        successUrl,
        cancelUrl,
        currentOrganization?.id,
      );

      window.location.href = session.url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error(
        error?.message || "Failed to start checkout process",
      );
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!plans.length) {
    return (
      <p className="font-secondary text-center text-sm text-white/55">
        No plans available at the moment.
      </p>
    );
  }

  return (
    <div className="w-full space-y-10">
      <div className="mx-auto flex max-w-[1280px] flex-col items-center text-center">
        <h2 className="font-title text-[36px] font-bold leading-tight text-white sm:text-[44px] md:text-[52px]">
          Choose your plan
        </h2>
        <p className="font-secondary mt-4 max-w-xl text-[17px] text-white/65 sm:text-[18px]">
          Select the perfect plan for your needs. Upgrade or downgrade anytime.
        </p>
      </div>

      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-5 overflow-visible py-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-3 lg:py-6">
        {plans.map((plan, index) => {
          const elevated = plans.length >= 2 && index === 1;
          const busy = isProcessing && selectedPlan?.id === plan.id;
          const features = buildPlanFeatures(plan);
          const subtitle =
            typeof plan.description === "string" ? plan.description : "";

          const isCurrent =
            currentPlanId != null && Number(plan.id) === Number(currentPlanId);

          // Contact-only plans (Custom / Enterprise tiers) can't be self-served.
          // Render a "Contact us" CTA that opens a mailto: link instead of going
          // through Stripe. Backend rejects POST subscriptions with 400 anyway.
          const isContactOnly = Boolean(plan.is_contact_only);
          const ctaLabel = isContactOnly
            ? "Contact us"
            : isCurrent
              ? "Current plan"
              : !isOrgOwner
                ? "Owner only"
                : "Subscribe now";

          return (
            <div
              key={plan.id}
              className={
                isCurrent
                  ? "rounded-sm ring-2 ring-primary/60 ring-offset-2 ring-offset-[#131313]"
                  : ""
              }
            >
              <PricingPlanCardLanding
                name={plan.name || "Plan"}
                subtitle={subtitle}
                price={isContactOnly ? "Custom" : planPriceDisplay(plan)}
                priceSuffix={isContactOnly ? "" : "per Month"}
                features={features}
                cta={ctaLabel}
                elevated={elevated}
                onCta={() => {
                  if (isCurrent) return;
                  if (isContactOnly) {
                    window.location.href =
                      `mailto:nodes@nodefleet.net?subject=${encodeURIComponent(
                        `Inquiry about ${plan.name} plan`,
                      )}&body=${encodeURIComponent(
                        `Hi,\n\nI'd like to learn more about the ${plan.name} plan and discuss requirements for my organization.\n\n`,
                      )}`;
                    return;
                  }
                  if (!isOrgOwner) return; // backend gates anyway; UI matches
                  handleSubscribe(plan);
                }}
                ctaDisabled={
                  (isProcessing && !isContactOnly) ||
                  isCurrent ||
                  (!isOrgOwner && !isContactOnly)
                }
                ctaLoading={busy && !isContactOnly}
                motionProps={{
                  initial: { opacity: 0, y: 16 },
                  animate: { opacity: 1, y: 0 },
                  transition: {
                    duration: 0.45,
                    ease: "easeOut",
                    delay: index * 0.05,
                  },
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="text-center font-secondary text-sm text-white/50">
        <p>All plans include access to all supported blockchains.</p>
        <p className="mt-1">
          Need a custom plan?{" "}
          <a
            href="mailto:nodes@nodefleet.net"
            className="font-semibold text-primary hover:text-primary-hover"
          >
            Contact us
          </a>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
